import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Capacitor } from "@capacitor/core";

import { Peer, type Message, type PeerState } from "@nav-games/multiplayer";
import { BleTransport } from "@nav-games/multiplayer-ble";
import { ITU_CODE } from "@nav-games/morse";

import { HangmanFigure } from "../../ui/HangmanFigure";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface HangmanMultiProps {
  onNavigate: (to: string) => void;
}

const STARTING_LIVES = 6;
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

type Role = "host" | "guest";

type Phase =
  | { kind: "pick-role" }
  | { kind: "connecting"; role: Role }
  | { kind: "host-picking-word"; peer: Peer }
  | { kind: "host-watching"; peer: Peer; word: string; guesses: string[]; lives: number; outcome: "won" | "lost" | null }
  | { kind: "guest-waiting"; peer: Peer }
  | { kind: "guest-playing"; peer: Peer; word: string; guessed: Set<string>; lives: number; outcome: "won" | "lost" | null }
  | { kind: "ended"; reason: string };

/// Multiplayer Hangman over BLE. Host picks a word; guest guesses
/// letter by letter using the same Morse-pattern keyboard as
/// single-player. Both sides see the same state because every guess
/// (and the resulting hit/miss + win/loss) is exchanged as a single
/// envelope. Native-only — web shows an "install the app" prompt.
export function HangmanMulti({ onNavigate }: HangmanMultiProps) {
  const native = Capacitor.isNativePlatform();
  const [phase, setPhase] = useState<Phase>({ kind: "pick-role" });
  const [helpOpen, setHelpOpen] = useState(false);
  // Stash one peer reference so cleanup doesn't depend on the
  // discriminated-union state holding it.
  const peerRef = useRef<Peer | null>(null);

  useEffect(() => {
    return () => {
      // Component unmount → close any open peer cleanly so the radio
      // doesn't keep advertising / scanning in the background.
      const p = peerRef.current;
      if (p) void p.close("page closed");
      peerRef.current = null;
    };
  }, []);

  const startSession = useCallback(async (role: Role) => {
    if (!native) return;
    setPhase({ kind: "connecting", role });
    const peer = new Peer({
      transport: new BleTransport(),
      role,
      gameType: "hangman",
    });
    peerRef.current = peer;

    peer.onStateChange((s: PeerState) => {
      if (s === "ready") {
        if (role === "host") {
          setPhase({ kind: "host-picking-word", peer });
        } else {
          setPhase({ kind: "guest-waiting", peer });
        }
      } else if (s === "closed") {
        setPhase({ kind: "ended", reason: "Connection closed." });
      }
    });

    peer.onMessage((m: Message) => {
      if (m.type !== "game") return;
      const payload = m.payload as Record<string, unknown>;
      if (typeof payload.type !== "string") return;
      if (payload.type === "word" && typeof payload.word === "string") {
        setPhase({
          kind: "guest-playing",
          peer,
          word: payload.word.toUpperCase(),
          guessed: new Set(),
          lives: STARTING_LIVES,
          outcome: null,
        });
      } else if (payload.type === "guess" && typeof payload.letter === "string") {
        setPhase((cur) => {
          if (cur.kind !== "host-watching") return cur;
          const letter = (payload.letter as string).toUpperCase();
          if (cur.guesses.includes(letter)) return cur;
          const hit = cur.word.includes(letter);
          const nextLives = hit ? cur.lives : cur.lives - 1;
          const nextGuesses = [...cur.guesses, letter];
          let outcome: "won" | "lost" | null = null;
          if (cur.word.split("").every((c) => nextGuesses.includes(c))) outcome = "won";
          else if (nextLives <= 0) outcome = "lost";
          return { ...cur, guesses: nextGuesses, lives: nextLives, outcome };
        });
      } else if (payload.type === "outcome") {
        setPhase((cur) => {
          if (cur.kind !== "guest-playing") return cur;
          return { ...cur, outcome: payload.won ? "won" : "lost" };
        });
      }
    });

    try {
      await peer.start();
    } catch (e) {
      setPhase({ kind: "ended", reason: (e as Error).message });
    }
  }, [native]);

  if (!native) {
    return (
      <div className="card">
        <h2>Multiplayer Hangman</h2>
        <p className="muted">
          BLE multiplayer needs the mobile app — Bluetooth peer-to-peer
          isn't available in browsers. Install the iOS or Android app to
          play this mode.
        </p>
        <button className="secondary" onClick={() => onNavigate("/play/morse")}>
          ← Back to Morse
        </button>
      </div>
    );
  }

  switch (phase.kind) {
    case "pick-role":
      return <PickRole onPick={startSession} onBack={() => onNavigate("/play/morse")} />;
    case "connecting":
      return <Connecting role={phase.role} />;
    case "host-picking-word":
      return <HostPickWord peer={phase.peer} onSent={(word) => {
        setPhase({
          kind: "host-watching",
          peer: phase.peer,
          word,
          guesses: [],
          lives: STARTING_LIVES,
          outcome: null,
        });
      }} />;
    case "host-watching":
      return <HostWatch state={phase} onDone={() => onNavigate("/play/morse")} />;
    case "guest-waiting":
      return (
        <div className="card">
          <h2>Connected</h2>
          <p className="muted">Waiting for your opponent to pick a word…</p>
        </div>
      );
    case "guest-playing":
      return (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <HelpButton onOpen={() => setHelpOpen(true)} />
          </div>
          <HelpPanel section={helpOpen ? "morse" : null} onClose={() => setHelpOpen(false)} />
          <GuestPlay state={phase} setPhase={setPhase} />
        </>
      );
    case "ended":
      return (
        <div className="card">
          <h2>Session ended</h2>
          <p className="muted">{phase.reason}</p>
          <button className="secondary" onClick={() => onNavigate("/play/morse")}>
            ← Back to Morse
          </button>
        </div>
      );
  }
}

function renderPattern(p: string): string {
  return p.replace(/\./g, "•").replace(/-/g, "—");
}

function PickRole({
  onPick,
  onBack,
}: {
  onPick: (role: Role) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Multiplayer Hangman</h2>
      <p className="muted">
        One player hosts and picks the word, the other guesses. Both phones
        must be on, with Bluetooth enabled, and physically close (~10 m).
      </p>
      <div className="card">
        <button onClick={() => onPick("host")} style={{ width: "100%", marginBottom: 8 }}>
          Host a game (pick the word)
        </button>
        <button onClick={() => onPick("guest")} className="secondary" style={{ width: "100%" }}>
          Join a game (guess the word)
        </button>
      </div>
      <button className="secondary" onClick={onBack}>← Back to Morse</button>
    </>
  );
}

function Connecting({ role }: { role: Role }) {
  return (
    <div className="card">
      <h2>{role === "host" ? "Waiting for opponent…" : "Looking for host…"}</h2>
      <p className="muted">
        Keep both phones close together with Bluetooth on. The link is
        anonymous — only this app's service UUID is broadcast.
      </p>
    </div>
  );
}

function HostPickWord({
  peer,
  onSent,
}: {
  peer: Peer;
  onSent: (word: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async () => {
    const upper = draft.trim().toUpperCase();
    if (upper.length < 3 || upper.length > 15) {
      setError("Pick a word between 3 and 15 letters.");
      return;
    }
    if (!/^[A-Z]+$/.test(upper)) {
      setError("Letters only, no spaces or digits.");
      return;
    }
    try {
      await peer.sendGame({ type: "word", word: upper });
      onSent(upper);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [draft, peer, onSent]);

  return (
    <>
      <h2>Pick a word for your opponent</h2>
      <div className="card">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="ANCHOR"
          autoCapitalize="characters"
          style={{ width: "100%", marginBottom: 12 }}
        />
        <button onClick={submit} style={{ width: "100%" }}>
          Send to opponent
        </button>
        {error && (
          <p className="small" style={{ color: "var(--danger)", marginTop: 8 }}>
            {error}
          </p>
        )}
      </div>
    </>
  );
}

function HostWatch({
  state,
  onDone,
}: {
  state: Extract<Phase, { kind: "host-watching" }>;
  onDone: () => void;
}) {
  const { peer, word, guesses, lives, outcome } = state;

  // Fire `outcome` to guest the moment the host computes a win/loss
  // so the guest's screen wraps up too.
  useEffect(() => {
    if (outcome) {
      void peer.sendGame({ type: "outcome", won: outcome === "won" });
    }
  }, [outcome, peer]);

  const reveal = word
    .split("")
    .map((c) => (guesses.includes(c) ? c : "_"))
    .join(" ");
  const wrongs = guesses.filter((c) => !word.includes(c));
  const wrongsCount = STARTING_LIVES - lives;

  return (
    <>
      <p className="small muted">
        You picked: <code>{word}</code>
      </p>
      <div
        className="card"
        style={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <HangmanFigure wrongs={wrongsCount} width={140} />
        <div
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 28,
            letterSpacing: "0.3em",
            margin: "12px 0",
          }}
        >
          {reveal}
        </div>
        {wrongs.length > 0 && (
          <p className="small muted">Wrong guesses: <code>{wrongs.join(" ")}</code></p>
        )}
      </div>
      {outcome && (
        <div className="card" style={{ textAlign: "center" }}>
          <h3>{outcome === "won" ? "They got it!" : "They ran out of lives."}</h3>
          <button onClick={onDone}>← Back to Morse</button>
        </div>
      )}
    </>
  );
}

function GuestPlay({
  state,
  setPhase,
}: {
  state: Extract<Phase, { kind: "guest-playing" }>;
  setPhase: (p: Phase) => void;
}) {
  const { peer, word, guessed, lives, outcome } = state;

  const onGuess = useCallback(
    async (letter: string) => {
      if (outcome || guessed.has(letter)) return;
      try {
        await peer.sendGame({ type: "guess", letter });
      } catch (e) {
        setPhase({ kind: "ended", reason: (e as Error).message });
        return;
      }
      const nextGuessed = new Set(guessed);
      nextGuessed.add(letter);
      const hit = word.includes(letter);
      const nextLives = hit ? lives : lives - 1;
      let nextOutcome: "won" | "lost" | null = null;
      if (word.split("").every((c) => nextGuessed.has(c))) nextOutcome = "won";
      else if (nextLives <= 0) nextOutcome = "lost";
      setPhase({
        kind: "guest-playing",
        peer,
        word,
        guessed: nextGuessed,
        lives: nextLives,
        outcome: nextOutcome,
      });
    },
    [guessed, lives, outcome, peer, setPhase, word],
  );

  const reveal = useMemo(
    () =>
      word
        .split("")
        .map((c) => (guessed.has(c) ? c : "_"))
        .join(" "),
    [word, guessed],
  );

  const wrongsCount = STARTING_LIVES - lives;

  return (
    <>
      <div
        className="card"
        style={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <HangmanFigure wrongs={wrongsCount} width={140} />
        <div
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 32,
            letterSpacing: "0.3em",
            margin: "12px 0",
            color:
              outcome === "lost"
                ? "var(--danger)"
                : outcome === "won"
                  ? "var(--ok)"
                  : undefined,
          }}
        >
          {outcome === "lost" ? word.split("").join(" ") : reveal}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))",
          gap: 8,
        }}
      >
        {ALPHABET.map((letter) => {
          const used = guessed.has(letter);
          const correct = used && word.includes(letter);
          return (
            <button
              key={letter}
              className="secondary"
              disabled={used || !!outcome}
              onClick={() => onGuess(letter)}
              style={{
                padding: "8px 4px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "center",
                background: correct
                  ? "rgba(44, 122, 59, 0.18)"
                  : used
                    ? "rgba(184, 48, 42, 0.12)"
                    : undefined,
              }}
            >
              <span style={{ fontFamily: "ui-monospace, monospace" }}>
                {renderPattern(ITU_CODE[letter]!)}
              </span>
              <span className="small muted">{letter}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
